import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, MapPin, Calendar, ArrowLeft, Image, Film, X, ChevronLeft, ChevronRight, Send, CheckCircle, Shield, Play, Users, Ticket, Clock, UserPlus, RotateCw, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.4 } }) };

function StarRating({ value, onChange, label }) {
  return (
    <div>
      <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block">{label}</label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(s => (
          <button key={s} type="button" onClick={() => onChange(s)} data-testid={`star-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}-${s}`}>
            <Star size={20} className={`transition-colors ${s <= value ? "text-gold fill-gold" : "text-white/20 hover:text-gold/50"}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function EventDetailPage() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [lightboxRotation, setLightboxRotation] = useState(0);
  const [showReview, setShowReview] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reviewForm, setReviewForm] = useState({ reviewer_name: "", reviewer_email: "", overall_rating: 0, ticketing_rating: 0, ambiance_rating: 0, bathroom_rating: 0, food_drink_rating: 0, liked: "", improve: "", consent_marketing: false });
  const [submitting, setSubmitting] = useState(false);
  const [attended, setAttended] = useState(false);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState("general");
  const [ticketName, setTicketName] = useState("");
  const [ticketEmail, setTicketEmail] = useState("");
  const [ticketQty, setTicketQty] = useState(1);
  const [buyingTicket, setBuyingTicket] = useState(false);
  const [rsvpName, setRsvpName] = useState("");
  const [rsvpEmail, setRsvpEmail] = useState("");
  const [rsvpDone, setRsvpDone] = useState(false);
  const [rsvpCount, setRsvpCount] = useState(0);
  const [countdown, setCountdown] = useState(null);

  const fetchEvent = () => { axios.get(`${API}/events/${eventId}`).then(r => setEvent(r.data)).catch(() => toast.error("Event not found")).finally(() => setLoading(false)); };
  useEffect(fetchEvent, [eventId]);
  useEffect(() => { setAttended(!!localStorage.getItem(`checkin_${eventId}`)); }, [eventId]);
  useEffect(() => { axios.get(`${API}/ticket-types`).then(r => setTicketTypes(r.data)).catch(() => {}); }, []);
  useEffect(() => { axios.get(`${API}/events/${eventId}/rsvps`).then(r => setRsvpCount(r.data.total_guests)).catch(() => {}); }, [eventId]);

  // Countdown timer
  useEffect(() => {
    if (!event?.event_date) return;
    const update = () => {
      const target = new Date(event.event_date + "T18:00:00");
      const now = new Date();
      const diff = target - now;
      if (diff <= 0) { setCountdown(null); return; }
      setCountdown({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        mins: Math.floor((diff % 3600000) / 60000),
        secs: Math.floor((diff % 60000) / 1000),
      });
    };
    update();
    const iv = setInterval(update, 1000);
    return () => clearInterval(iv);
  }, [event?.event_date]);

  const images = event?.images || [];
  const videos = event?.videos || [];
  const reviews = event?.reviews || [];
  const allMedia = [...images, ...videos];

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.reviewer_name || !reviewForm.reviewer_email || !reviewForm.overall_rating) { toast.error("Name, email, and overall rating required."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, { ...reviewForm, item_id: eventId });
      setSubmitted(true);
      toast.success("Review submitted!");
      fetchEvent();
    } catch { toast.error("Failed."); } finally { setSubmitting(false); }
  };

  const buyTicket = async () => {
    if (!ticketName.trim() || !ticketEmail.trim()) { toast.error("Name and email required."); return; }
    setBuyingTicket(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/tickets/purchase`, {
        event_id: eventId, buyer_name: ticketName, buyer_email: ticketEmail,
        ticket_type: selectedTicket, quantity: ticketQty,
        origin_url: window.location.origin
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch { toast.error("Failed to purchase tickets."); } finally { setBuyingTicket(false); }
  };

  const handleRsvp = async () => {
    if (!rsvpName.trim() || !rsvpEmail.trim()) { toast.error("Name and email required."); return; }
    try {
      const res = await axios.post(`${API}/events/${eventId}/rsvp`, { name: rsvpName, email: rsvpEmail, guests: 1 });
      if (res.data.already) toast.info("You've already RSVP'd!");
      else toast.success("RSVP confirmed!");
      setRsvpDone(true);
      setRsvpCount(c => c + 1);
    } catch { toast.error("RSVP failed."); }
  };

  if (loading) return <div className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center"><div className="text-gold animate-pulse font-body">Loading event...</div></div>;
  if (!event) return null;

  return (
    <div data-testid="event-detail-page" className="pt-20 min-h-screen bg-luxury-black">
      {/* Hero Cover */}
      {event.cover_image && (
        <div className="relative h-[50vh] overflow-hidden">
          <img src={event.cover_image} alt={event.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-luxury-black via-luxury-black/50 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 max-w-5xl mx-auto">
            <span className="px-3 py-1 bg-gold/20 text-gold text-[10px] font-body uppercase tracking-wider">{event.event_type}</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-light text-white mt-3" data-testid="event-title">{event.title}</h1>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        <Link to="/events" className="inline-flex items-center gap-2 text-white/40 hover:text-gold text-sm font-body mb-8 transition-colors"><ArrowLeft size={16} /> All Events</Link>

        {!event.cover_image && <h1 className="font-heading text-4xl text-white mb-4">{event.title}</h1>}

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-6 mb-6 text-white/40 text-sm font-body">
          {event.event_date && <span className="flex items-center gap-2"><Calendar size={14} className="text-gold" /> {event.event_date}</span>}
          {event.location && <span className="flex items-center gap-2"><MapPin size={14} className="text-gold" /> {event.location}</span>}
          <span className="flex items-center gap-2"><Image size={14} className="text-gold" /> {images.length} photos</span>
          <span className="flex items-center gap-2"><Film size={14} className="text-gold" /> {videos.length} videos</span>
          {event.attendance > 0 && <span className="flex items-center gap-2"><Users size={14} className="text-gold" /> {event.attendance} attended</span>}
          {event.avg_rating > 0 && <span className="flex items-center gap-2"><Star size={14} className="text-gold fill-gold" /> {event.avg_rating}/5 ({event.review_count} reviews)</span>}
        </div>
        {event.description && <p className="text-white/60 font-body font-light text-sm leading-relaxed mb-8">{event.description}</p>}

        {/* Countdown Timer */}
        {countdown && (
          <div className="mb-10 glass-card p-6" data-testid="event-countdown">
            <div className="flex items-center gap-2 mb-4"><Clock size={16} className="text-gold" /><span className="text-gold text-[10px] font-body uppercase tracking-wider">Event Countdown</span></div>
            <div className="flex items-center justify-center gap-6">
              {[["days", countdown.days], ["hours", countdown.hours], ["mins", countdown.mins], ["secs", countdown.secs]].map(([label, val]) => (
                <div key={label} className="text-center">
                  <span className="text-gold font-heading text-3xl block" data-testid={`countdown-${label}`}>{String(val).padStart(2, "0")}</span>
                  <span className="text-white/30 font-body text-[10px] uppercase tracking-wider">{label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RSVP Section */}
        <div className="mb-10 glass-card p-6" data-testid="rsvp-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><UserPlus size={16} className="text-gold" /><span className="text-gold text-[10px] font-body uppercase tracking-wider">RSVP</span></div>
            {rsvpCount > 0 && <span className="text-white/30 text-xs font-body">{rsvpCount} guests confirmed</span>}
          </div>
          {!rsvpDone ? (
            <div className="flex flex-wrap gap-3 items-end">
              <Input data-testid="rsvp-name" value={rsvpName} onChange={e => setRsvpName(e.target.value)} placeholder="Your name" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-sm flex-1 min-w-[150px]" />
              <Input data-testid="rsvp-email" type="email" value={rsvpEmail} onChange={e => setRsvpEmail(e.target.value)} placeholder="your@email.com" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-sm flex-1 min-w-[150px]" />
              <button onClick={handleRsvp} data-testid="rsvp-submit" className="bg-gold text-black font-body font-medium text-xs px-6 h-10 hover:bg-gold-light transition-colors">RSVP Now</button>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-green-400 font-body text-sm"><CheckCircle size={16} /> You're on the guest list!</div>
          )}
        </div>

        {/* Buy Tickets */}
        {ticketTypes.length > 0 && (
          <div className="mb-12 glass-card p-8" data-testid="ticket-purchase-section">
            <h2 className="font-heading text-2xl text-white mb-6 flex items-center gap-2"><Ticket size={20} className="text-gold" /> Get Tickets</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              {ticketTypes.map(t => (
                <button key={t.id} onClick={() => setSelectedTicket(t.id)} data-testid={`ticket-type-${t.id}`} className={`p-4 border text-left transition-all ${selectedTicket === t.id ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/20"}`}>
                  <p className="text-white font-body text-sm font-medium">{t.name}</p>
                  <p className="text-gold font-body text-lg font-medium mt-1">${t.price.toFixed(2)}</p>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
              <Input data-testid="ticket-buyer-name-input" value={ticketName} onChange={e => setTicketName(e.target.value)} placeholder="Your name" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11 rounded-sm text-sm" />
              <Input data-testid="ticket-buyer-email-input" type="email" value={ticketEmail} onChange={e => setTicketEmail(e.target.value)} placeholder="your@email.com" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11 rounded-sm text-sm" />
              <div className="flex items-center gap-3">
                <label className="text-white/40 text-xs font-body">Qty</label>
                <Input data-testid="ticket-qty-input" type="number" min={1} max={10} value={ticketQty} onChange={e => setTicketQty(Math.max(1, parseInt(e.target.value) || 1))} className="bg-white/5 border-white/10 text-white h-11 rounded-sm text-sm w-20" />
                <span className="text-gold font-body text-sm font-medium ml-auto">${((ticketTypes.find(t => t.id === selectedTicket)?.price || 0) * ticketQty).toFixed(2)}</span>
              </div>
            </div>
            <button onClick={buyTicket} disabled={buyingTicket} data-testid="buy-ticket-btn" className="inline-flex items-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-3 hover:bg-gold-light transition-colors disabled:opacity-50">
              {buyingTicket ? "Processing..." : "Purchase Tickets"} <Ticket size={16} />
            </button>
          </div>
        )}

        {/* Photos Gallery — Instagram-style: 3/4/5 cols, square, 1px gaps */}
        {images.length > 0 && (
          <div className="mb-12">
            <h2 className="font-heading text-2xl text-white mb-6 flex items-center gap-2"><Image size={20} className="text-gold" /> Photos</h2>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1 sm:gap-1.5">
              {images.map((img, i) => (
                <motion.div key={img.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} className="aspect-square overflow-hidden cursor-pointer group bg-luxury-surface" onClick={() => setLightbox(i)} data-testid={`event-photo-${img.id}`}>
                  <img src={img.url} alt={img.filename} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 pointer-events-none" />
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Videos */}
        {videos.length > 0 && (
          <div className="mb-12">
            <h2 className="font-heading text-2xl text-white mb-6 flex items-center gap-2"><Film size={20} className="text-gold" /> Videos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {videos.map((vid, i) => (
                <motion.div key={vid.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} className="aspect-video bg-luxury-surface overflow-hidden" data-testid={`event-video-${vid.id}`}>
                  {vid.url.includes("youtube") || vid.url.includes("youtu.be") ? (
                    <iframe src={vid.url.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/")} className="w-full h-full" allow="accelerometer; autoplay; encrypted-media; gyroscope" allowFullScreen title={vid.filename} />
                  ) : vid.url.includes("vimeo") ? (
                    <iframe src={vid.url.replace("vimeo.com/", "player.vimeo.com/video/")} className="w-full h-full" allowFullScreen title={vid.filename} />
                  ) : (
                    <video src={vid.url} controls className="w-full h-full object-cover" />
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Attended Banner */}
        {attended && !submitted && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-8 p-5 border border-gold/20 bg-gold/[0.05] flex items-center justify-between" data-testid="attended-banner">
            <div className="flex items-center gap-3">
              <CheckCircle size={20} className="text-gold" />
              <div>
                <p className="text-white font-body text-sm font-medium">You attended this event!</p>
                <p className="text-white/40 font-body text-xs">Share your experience — your feedback helps the community.</p>
              </div>
            </div>
            {!showReview && <button onClick={() => setShowReview(true)} data-testid="attended-review-btn" className="flex items-center gap-2 bg-gold text-black font-body font-medium text-xs px-5 py-2 hover:bg-gold-light transition-colors"><Star size={12} /> Leave a Review</button>}
          </motion.div>
        )}

        {/* Reviews Section */}
        <div className="border-t border-white/10 pt-10">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-2xl text-white">Guest Reviews ({reviews.length})</h2>
            {!submitted && <button data-testid="write-event-review-btn" onClick={() => setShowReview(!showReview)} className="flex items-center gap-2 bg-gold/15 text-gold px-5 py-2 text-xs font-body hover:bg-gold/25 transition-colors"><Star size={14} /> Leave a Review</button>}
          </div>

          {showReview && !submitted && (
            <motion.form initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} onSubmit={submitReview} className="glass-card p-8 mb-10 space-y-6" data-testid="event-review-form">
              <h3 className="text-white font-body text-sm font-medium">How was your experience at {event.title}?</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div><label className="text-white/50 text-xs font-body uppercase mb-2 block">Name *</label><Input data-testid="event-review-name" value={reviewForm.reviewer_name} onChange={e => setReviewForm({...reviewForm, reviewer_name: e.target.value})} placeholder="Your name" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-sm" /></div>
                <div><label className="text-white/50 text-xs font-body uppercase mb-2 block">Email *</label><Input data-testid="event-review-email" type="email" value={reviewForm.reviewer_email} onChange={e => setReviewForm({...reviewForm, reviewer_email: e.target.value})} placeholder="your@email.com" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
                <StarRating label="Overall *" value={reviewForm.overall_rating} onChange={v => setReviewForm({...reviewForm, overall_rating: v})} />
                <StarRating label="Ticketing" value={reviewForm.ticketing_rating} onChange={v => setReviewForm({...reviewForm, ticketing_rating: v})} />
                <StarRating label="Ambiance" value={reviewForm.ambiance_rating} onChange={v => setReviewForm({...reviewForm, ambiance_rating: v})} />
                <StarRating label="Bathroom" value={reviewForm.bathroom_rating} onChange={v => setReviewForm({...reviewForm, bathroom_rating: v})} />
                <StarRating label="Food & Drink" value={reviewForm.food_drink_rating} onChange={v => setReviewForm({...reviewForm, food_drink_rating: v})} />
              </div>
              <div><label className="text-white/50 text-xs font-body uppercase mb-2 block">What did you enjoy?</label><Textarea data-testid="event-review-liked" value={reviewForm.liked} onChange={e => setReviewForm({...reviewForm, liked: e.target.value})} placeholder="What stood out..." rows={3} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-sm text-sm resize-none" /></div>
              <div><label className="text-white/50 text-xs font-body uppercase mb-2 block">What could be improved?</label><Textarea data-testid="event-review-improve" value={reviewForm.improve} onChange={e => setReviewForm({...reviewForm, improve: e.target.value})} placeholder="Constructive feedback..." rows={3} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-sm text-sm resize-none" /></div>
              <div className="p-4 bg-white/[0.02] border border-white/5 flex items-start gap-3">
                <Shield size={16} className="text-gold flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white/50 text-xs font-body"><strong className="text-white/70">Your feedback is fed to our AI system to improve services.</strong> Your personal information is never sold to third parties without consent.</p>
                  <label className="flex items-center gap-2 mt-2 cursor-pointer"><input type="checkbox" checked={reviewForm.consent_marketing} onChange={e => setReviewForm({...reviewForm, consent_marketing: e.target.checked})} className="accent-gold" /><span className="text-white/40 text-xs font-body">Send me curated event invitations (optional)</span></label>
                </div>
              </div>
              <button type="submit" disabled={submitting} data-testid="submit-event-review" className="inline-flex items-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-3 hover:bg-gold-light transition-colors disabled:opacity-50">{submitting ? "Submitting..." : "Submit Review"} <Send size={16} /></button>
            </motion.form>
          )}

          {submitted && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card p-8 mb-10 text-center">
              <CheckCircle size={36} className="text-gold mx-auto mb-3" />
              <p className="text-white font-heading text-xl mb-2">Thank You!</p>
              <p className="text-white/50 font-body text-sm">Your feedback helps event promoters improve and helps our community find the best experiences.</p>
            </motion.div>
          )}

          {reviews.length === 0 ? <p className="text-white/30 font-body text-sm text-center py-8">No reviews yet. Be the first to share your experience.</p> : (
            <div className="space-y-5">
              {reviews.map((r, i) => (
                <motion.div key={r.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} className="glass-card p-6" data-testid={`event-review-${r.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div><p className="text-white font-body text-sm font-medium">{r.reviewer_name}</p><p className="text-white/30 text-xs font-body">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : ""}</p></div>
                    <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, j) => <Star key={j} size={12} className={j < r.overall_rating ? "text-gold fill-gold" : "text-white/15"} />)}</div>
                  </div>
                  <div className="flex flex-wrap gap-4 mb-3 text-white/30 text-[10px] font-body">
                    {r.ticketing_rating > 0 && <span>Ticketing: {r.ticketing_rating}/5</span>}
                    {r.ambiance_rating > 0 && <span>Ambiance: {r.ambiance_rating}/5</span>}
                    {r.bathroom_rating > 0 && <span>Bathroom: {r.bathroom_rating}/5</span>}
                    {r.food_drink_rating > 0 && <span>Food & Drink: {r.food_drink_rating}/5</span>}
                  </div>
                  {r.liked && <p className="text-white/60 font-body text-xs mb-1"><span className="text-gold">Liked:</span> {r.liked}</p>}
                  {r.improve && <p className="text-white/40 font-body text-xs"><span className="text-white/50">Improve:</span> {r.improve}</p>}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && images[lightbox] && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center" onClick={() => { setLightbox(null); setLightboxRotation(0); }} data-testid="event-lightbox">
            <button onClick={e => { e.stopPropagation(); setLightbox(null); setLightboxRotation(0); }} className="absolute top-6 right-6 text-white/60 hover:text-white z-10" data-testid="lightbox-close"><X size={28} /></button>
            <button onClick={e => { e.stopPropagation(); setLightbox((lightbox - 1 + images.length) % images.length); setLightboxRotation(0); }} className="absolute left-6 top-1/2 -translate-y-1/2 text-white/60 hover:text-white z-10"><ChevronLeft size={36} /></button>
            <button onClick={e => { e.stopPropagation(); setLightbox((lightbox + 1) % images.length); setLightboxRotation(0); }} className="absolute right-6 top-1/2 -translate-y-1/2 text-white/60 hover:text-white z-10"><ChevronRight size={36} /></button>
            {/* Rotate controls — for fixing sideways legacy photos (CSS transform, view-only) */}
            <div onClick={e => e.stopPropagation()} className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
              <button data-testid="lightbox-rotate-left" onClick={() => setLightboxRotation(r => (r - 90) % 360)} className="flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-gold/30 border border-white/15 text-white text-xs font-body uppercase tracking-wider transition-colors" title="Rotate left 90°"><RotateCcw size={14} /> Left</button>
              <button data-testid="lightbox-rotate-right" onClick={() => setLightboxRotation(r => (r + 90) % 360)} className="flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-gold/30 border border-white/15 text-white text-xs font-body uppercase tracking-wider transition-colors" title="Rotate right 90°"><RotateCw size={14} /> Right</button>
              {lightboxRotation !== 0 && (
                <button data-testid="lightbox-rotate-reset" onClick={() => setLightboxRotation(0)} className="px-3 py-2 bg-gold/15 hover:bg-gold/25 border border-gold/40 text-gold text-xs font-body uppercase tracking-wider transition-colors">Reset</button>
              )}
            </div>
            <div onClick={e => e.stopPropagation()} className="max-w-5xl max-h-[85vh] px-4">
              <img
                data-testid="lightbox-image"
                src={images[lightbox].url}
                alt={images[lightbox].filename}
                style={{ transform: `rotate(${lightboxRotation}deg)` }}
                className="max-w-full max-h-[80vh] object-contain transition-transform duration-300"
              />
              <p className="text-center text-white/40 text-xs font-body mt-3">{lightbox + 1} of {images.length}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
